import React, { useState, useMemo, useEffect } from 'react';
import {
  Add as AddIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  Phone as PhoneIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  HelpOutline as HelpIcon,
  DeleteOutline as DeleteIcon,
  LocationOn as LocationIcon,
  ViewTimeline as TemplateIcon,
} from '@mui/icons-material';
import { Tooltip, Autocomplete, TextField } from '@mui/material';
import { useLazyQuery } from '@apollo/client';
import CustomButton from '../../../components/common/Button';
import PreAssignedDriversSelector from './PreAssignedDriversSelector';
import { Shift, Driver, Assignment, AssignmentStatus } from '../types';
import { getStatusColor, formatDate, formatDateTime } from '../utils';
import { FETCH_ALL_SHIFTS } from '../../../api/mutations/ShiftManagementMutations';
import { stagingOptions } from '../../../constants/apollo';
import { ShiftTemplateFilter } from './ShiftTemplateFilter';

interface DriverAssignmentViewProps {
  shifts: Shift[];
  drivers: Driver[];
  assignments: Assignment[];
  selectedShiftId: string;
  selectedDriverIds: string[];
  onShiftChange: (shiftId: string) => void;
  onDriverToggle: (driverId: string) => void;
  onAssignDrivers: () => void;
  onUnassignDriver: (shiftId: string, driverId: string) => void;
  onUpdateAssignmentStatus?: (shiftId: string, driverId: string, status: AssignmentStatus) => void;
  getAssignedDriversForShift: (shiftId: string) => string[];
  getAvailableDrivers: () => Driver[];
  onLoadMoreDrivers?: () => void;
  hasMoreDrivers?: boolean;
  driversLoading?: boolean;
  driverSearchQuery?: string;
  onDriverSearchChange?: (query: string) => void;
  selectedShift?: Shift;
}

const DRIVER_ASSIGNMENT_STORAGE_KEY = 'driverAssignmentViewState';

// Helper function to load cached state
const loadCachedDriverAssignmentState = () => {
  try {
    const cached = localStorage.getItem(DRIVER_ASSIGNMENT_STORAGE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (error) {
    console.error('Error loading cached state:', error);
  }
  return null;
};

// Helper function to save state to cache
const saveCachedDriverAssignmentState = (state: any) => {
  try {
    localStorage.setItem(DRIVER_ASSIGNMENT_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Error saving state to cache:', error);
  }
};

export const DriverAssignmentView: React.FC<DriverAssignmentViewProps> = ({
  shifts,
  drivers,
  assignments,
  selectedShiftId,
  selectedDriverIds,
  onShiftChange,
  onDriverToggle,
  onAssignDrivers,
  onUnassignDriver,
  onUpdateAssignmentStatus,
  getAssignedDriversForShift,
  getAvailableDrivers,
  onLoadMoreDrivers,
  hasMoreDrivers = false,
  driversLoading = false,
  driverSearchQuery = '',
  onDriverSearchChange,
  selectedShift: propSelectedShift,
}) => {
  const cachedState = loadCachedDriverAssignmentState();

  const [shiftSearchQuery, setShiftSearchQuery] = useState(cachedState?.shiftSearchQuery || '');
  const [shiftPage, setShiftPage] = useState(cachedState?.shiftPage || 0);
  const [shiftTemplateId, setShiftTemplateId] = useState<string | undefined>(cachedState?.shiftTemplateId || undefined);
  const [allShifts, setAllShifts] = useState<Array<{
    id: string;
    name: string;
    shiftTemplateId?: string;
    shiftTemplateName?: string;
    zone: string;
    status: string;
    startTime: string;
    endTime: string;
    requiredDrivers: number;
    maxDrivers: number;
    cityId: string;
    neighborhoodId?: string;
    assignedDriversCount: number;
    availableSlots: number;
  }>>([]);
  const [shiftTotalCount, setShiftTotalCount] = useState(0);
  const [debouncedShiftSearchQuery, setDebouncedShiftSearchQuery] = useState(cachedState?.debouncedShiftSearchQuery || '');
  const [selectedShiftOption, setSelectedShiftOption] = useState<{
    id: string;
    label: string;
    searchText?: string;
    name: string;
    shiftTemplateId?: string;
    shiftTemplateName?: string;
    zone: string;
    status: string;
    startTime: string;
    endTime: string;
    requiredDrivers: number;
    maxDrivers: number;
    cityId: string;
    neighborhoodId?: string;
    assignedDriversCount: number;
    availableSlots: number;
  } | null>(cachedState?.selectedShiftOption || null);

  const SHIFTS_PER_PAGE = 20;
  const [templateNameCache, setTemplateNameCache] = useState<{ [key: string]: string }>({});

  const [fetchShiftsQuery, { loading: shiftsLoading }] = useLazyQuery<{
    fetchAllShifts: {
      items: Array<{
        id: string;
        name?: string;
        startTime: string;
        endTime: string;
        requiredDrivers?: number;
        status?: string;
        city?: { id: string; name: string };
        neighborhood?: { id: string; name: string };
        assignedDriversCount?: number;
        availableSlots?: number;
        shiftTemplate?: { id: string; name?: string };
      }>;
      totalCount: number;
    };
  }>(FETCH_ALL_SHIFTS, {
    ...stagingOptions,
    fetchPolicy: 'network-only',
  });
  // Cache state to localStorage whenever key values change
  useEffect(() => {
    saveCachedDriverAssignmentState({
      shiftSearchQuery,
      shiftPage,
      debouncedShiftSearchQuery,
      selectedShiftOption,
      shiftTemplateId,
    });
  }, [shiftSearchQuery, shiftPage, debouncedShiftSearchQuery, selectedShiftOption, shiftTemplateId]);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedShiftSearchQuery(shiftSearchQuery);
      setShiftPage(0);
      setAllShifts([]);
    }, 500);

    return () => clearTimeout(timer);
  }, [shiftSearchQuery]);

  // Reset page when shiftTemplateId filter changes
  useEffect(() => {
    setShiftPage(0);
    setAllShifts([]);
  }, [shiftTemplateId]);

  useEffect(() => {
    const loadShifts = async () => {
      try {
        const { data: shiftsData } = await fetchShiftsQuery({
          variables: {
            limit: SHIFTS_PER_PAGE,
            offset: shiftPage * SHIFTS_PER_PAGE,
            order: 'desc',
            status: undefined,
            search: debouncedShiftSearchQuery || undefined,
            shiftTemplateId: shiftTemplateId || undefined,
          },
        });

        if (shiftsData?.fetchAllShifts) {
          let filteredShifts = shiftsData.fetchAllShifts.items.filter((shift: any) => {
            const status = (shift.status || '').toUpperCase();
            return status === 'DRAFT' || status === 'ACTIVE';
          });

          const transformedShifts = filteredShifts.map((shift: any) => {
            const shiftName = shift.name || `Shift ${shift.id}`;
            const zone = shift.neighborhood?.name || shift.city?.name || 'No Zone';
            const status = shift.status?.toLowerCase() || 'draft';

            return {
              id: shift.id,
              name: shiftName,
              shiftTemplateId: shift.shiftTemplate?.id,
              shiftTemplateName: shift.shiftTemplate?.name,
              zone: zone,
              status: status,
              startTime: shift.startTime,
              endTime: shift.endTime,
              requiredDrivers: shift.requiredDrivers || 1,
              maxDrivers: shift.requiredDrivers || 1,
              cityId: shift.city?.id || '',
              neighborhoodId: shift.neighborhood?.id || undefined,
              assignedDriversCount: shift.assignedDriversCount || 0,
              availableSlots: shift.availableSlots || (shift.requiredDrivers || 1),
            };
          });

          if (shiftPage === 0) {
            setAllShifts(transformedShifts);
          } else {
            setAllShifts((prev) => [...prev, ...transformedShifts]);
          }
          setShiftTotalCount(shiftsData.fetchAllShifts.totalCount);
        }
      } catch (err) {
        console.error('Error fetching shifts:', err);
      }
    };

    loadShifts();
  }, [debouncedShiftSearchQuery, shiftPage, shiftTemplateId, fetchShiftsQuery]);

  useEffect(() => {
    if (selectedShiftId) {
      const shift = allShifts.find((s) => s.id === selectedShiftId);
      if (shift) {
        setSelectedShiftOption({
          id: shift.id,
          label: `${shift.name} - ${shift.zone} (${shift.status})`,
          searchText: `${shift.id} ${shift.name} ${shift.zone} ${shift.status} ${shift.shiftTemplateName || ''}`.toLowerCase(),
          name: shift.name,
          shiftTemplateId: shift.shiftTemplateId,
          shiftTemplateName: shift.shiftTemplateName,
          zone: shift.zone,
          status: shift.status,
          startTime: shift.startTime,
          endTime: shift.endTime,
          requiredDrivers: shift.requiredDrivers,
          maxDrivers: shift.maxDrivers,
          cityId: shift.cityId,
          neighborhoodId: shift.neighborhoodId,
          assignedDriversCount: shift.assignedDriversCount,
          availableSlots: shift.availableSlots,
        });
      }
    } else {
      setSelectedShiftOption(null);
    }
  }, [selectedShiftId, allShifts]);

  const shiftOptions = useMemo(() => {
    if (!allShifts || allShifts.length === 0) {
      if (selectedShiftOption) {
        return [selectedShiftOption];
      }
      return [];
    }

    const options = allShifts.map((shift) => {
      const baseLabel = `${shift.name} - ${shift.zone} (${shift.status})`;
      const templatePart = shift.shiftTemplateId
        ? ` • Template: ${shift.shiftTemplateName || shift.shiftTemplateId}`
        : '';

      return {
        id: shift.id,
        label: `${baseLabel}${templatePart}`,
        searchText: `${shift.id} ${shift.name} ${shift.zone} ${shift.status} ${shift.shiftTemplateName || ''}`.toLowerCase(),
        name: shift.name,
        shiftTemplateId: shift.shiftTemplateId,
        shiftTemplateName: shift.shiftTemplateName,
        zone: shift.zone,
        status: shift.status,
        startTime: shift.startTime,
        endTime: shift.endTime,
        requiredDrivers: shift.requiredDrivers,
        maxDrivers: shift.maxDrivers,
        cityId: shift.cityId,
        neighborhoodId: shift.neighborhoodId ?? undefined,
        assignedDriversCount: shift.assignedDriversCount,
        availableSlots: shift.availableSlots,
      };
    });

    if (selectedShiftOption && !options.find(opt => opt.id === selectedShiftOption.id)) {
      options.unshift({
        id: selectedShiftOption.id,
        label: selectedShiftOption.label,
        searchText: `${selectedShiftOption.id} ${selectedShiftOption.name} ${selectedShiftOption.zone} ${selectedShiftOption.status}`.toLowerCase(),
        name: selectedShiftOption.name,
        shiftTemplateId: selectedShiftOption.shiftTemplateId,
        shiftTemplateName: selectedShiftOption.shiftTemplateName,
        zone: selectedShiftOption.zone,
        status: selectedShiftOption.status,
        startTime: selectedShiftOption.startTime,
        endTime: selectedShiftOption.endTime,
        requiredDrivers: selectedShiftOption.requiredDrivers,
        maxDrivers: selectedShiftOption.maxDrivers,
        cityId: selectedShiftOption.cityId,
        neighborhoodId: selectedShiftOption.neighborhoodId ?? undefined,
        assignedDriversCount: selectedShiftOption.assignedDriversCount,
        availableSlots: selectedShiftOption.availableSlots,
      });
    }

    return options;
  }, [allShifts, selectedShiftOption]);

  const hasMoreShifts = allShifts.length < shiftTotalCount;

  const handleLoadMoreShifts = () => {
    if (!shiftsLoading && hasMoreShifts) {
      setShiftPage((prev: number) => prev + 1);
    }
  };

  const selectedShift = propSelectedShift || shifts.find((s) => s.id === selectedShiftId) || (selectedShiftOption ? {
    id: selectedShiftOption.id,
    name: selectedShiftOption.name,
    shiftTemplateId: selectedShiftOption.shiftTemplateId,
    shiftTemplateName: selectedShiftOption.shiftTemplateName,
    zone: selectedShiftOption.zone,
    status: selectedShiftOption.status,
    startTime: selectedShiftOption.startTime,
    endTime: selectedShiftOption.endTime,
    requiredDrivers: selectedShiftOption.requiredDrivers,
    maxDrivers: selectedShiftOption.maxDrivers,
    cityId: selectedShiftOption.cityId,
    neighborhoodId: selectedShiftOption.neighborhoodId,
    assignedDriversCount: selectedShiftOption.assignedDriversCount,
    availableSlots: selectedShiftOption.availableSlots,
  } : undefined);
  const availableDrivers = getAvailableDrivers();
  const assignedDriversForShift = selectedShiftId ? getAssignedDriversForShift(selectedShiftId) : [];
  // Create a map of driver IDs to their assignment statuses for the selected shift
  const assignedDriverStatuses = useMemo(() => {
    if (!selectedShiftId) return {};
    const statusMap: { [driverId: string]: string } = {};
    assignments
      .filter(a => a.shiftId === selectedShiftId && a.courier?.id)
      .forEach(a => {
        if (a.courier?.id) {
          statusMap[a.courier.id] = a.status || 'pending';
        }
      });
    return statusMap;
  }, [selectedShiftId, assignments]);
  // Calculate assigned count excluding cancelled assignments
  const nonCancelledAssignments = selectedShiftId 
    ? assignments.filter(a => a.shiftId === selectedShiftId && a.status !== 'cancelled')
    : [];
  const assignedCount = nonCancelledAssignments.length;
  const maxDrivers = selectedShift?.requiredDrivers || selectedShift?.maxDrivers || 0;
  const utilizationRate = maxDrivers > 0 ? Math.round((assignedCount / maxDrivers) * 100) : 0;
  const remainingSlots = maxDrivers - assignedCount;

  const handleDriverIdsChange = (driverIds: string[]) => {
    const currentIds = new Set(selectedDriverIds);
    const newIds = new Set(driverIds);

    currentIds.forEach(id => {
      if (!newIds.has(id)) {
        onDriverToggle(id);
      }
    });

    newIds.forEach(id => {
      if (!currentIds.has(id)) {
        onDriverToggle(id);
      }
    });
  };

  return (
    <div className="main-wrapper">
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <h2 className="pageTitle mb-1 text-xl font-bold">Driver Assignment</h2>
          <Tooltip title="Select a shift, then choose drivers to assign. You can search and filter drivers to find the right ones quickly." arrow>
            <InfoIcon sx={{ fontSize: '18px', color: '#9CA3AF', cursor: 'help' }} />
          </Tooltip>
        </div>
        <p className="text-sm text-gray-500">
          Assign drivers to shifts efficiently with search and filter options
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
        {/* Left Column - Shift Selection */}
        <div className="bg-gradient-to-br from-white to-gray-50/50 rounded-lg border border-gray-200 shadow-sm p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <ScheduleIcon sx={{ color: '#3B82F6', fontSize: '18px' }} />
              <h3 className="text-sm font-semibold text-gray-900">Select Shift Session</h3>
            </div>
            <div className="flex items-center gap-2">
              {selectedShiftId && (
                <button
                  onClick={() => {
                    setSelectedShiftOption(null);
                    onShiftChange('');
                    setShiftTemplateId(undefined);
                  }}
                  className="text-xs text-gray-600 hover:text-gray-800 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                >
                  <CloseIcon sx={{ fontSize: '14px', marginRight: '2px', verticalAlign: 'middle' }} />
                  Clear
                </button>
              )}
              <Tooltip title="Only draft or active shifts can accept new driver assignments" arrow>
                <HelpIcon sx={{ fontSize: '14px', color: '#9CA3AF', cursor: 'help' }} />
              </Tooltip>
            </div>
          </div>

          {/* Shift Schedule Filter */}
          <div className="mb-3">
            <ShiftTemplateFilter
              value={shiftTemplateId}
              onChange={(value) => {
                setShiftTemplateId(value);
                  // Clear the shift selection when scheduler filter changes
                  setSelectedShiftOption(null);
                  onShiftChange('');
                  setShiftSearchQuery('');
                }}
                    placeholder="All Shift Schedules"
              tooltipTitle="Filter shifts by shift schedule"
              onTemplateNameChange={(templateId, templateName) => {
                if (templateName) {
                  setTemplateNameCache((prev) => ({ ...prev, [templateId]: templateName }));
                }
                }}
              />
            {shiftTemplateId && (
              <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 bg-primaryColor/10 text-primaryColor rounded-lg text-xs font-medium border border-primaryColor/20">
                <TemplateIcon sx={{ fontSize: '14px' }} />
                <span>
                  {(() => {
                    const name = templateNameCache[shiftTemplateId] || `Shift Schedule ${shiftTemplateId}`;
                    return `${name} (${shiftTemplateId})`;
                  })()}
                </span>
                <Tooltip title="Remove filter" arrow>
                  <button
                    onClick={() => setShiftTemplateId(undefined)}
                    className="ml-1 hover:bg-primaryColor/20 rounded p-0.5 transition-colors"
                  >
                    <CloseIcon sx={{ fontSize: '14px' }} />
                  </button>
                </Tooltip>
              </div>
            )}
          </div>

          <div>
            <Autocomplete
              options={shiftOptions}
              getOptionLabel={(option) => option.label}
              filterOptions={(options, { inputValue }) => {
                const searchValue = inputValue.toLowerCase().trim();
                if (!searchValue) return options;

                return options.filter((option) => {
                  // Search by ID or name only
                  return option.id.toLowerCase().includes(searchValue) ||
                    option.name.toLowerCase().includes(searchValue);
                });
              }}
              value={selectedShiftOption}
              onChange={(event, newValue) => {
                if (newValue) {
                  setSelectedShiftOption(newValue);
                  onShiftChange(newValue.id);
                } else {
                  setSelectedShiftOption(null);
                  onShiftChange('');
                }
              }}
              onInputChange={(event, newInputValue) => {
                setShiftSearchQuery(newInputValue);
              }}
              loading={shiftsLoading}
              ListboxProps={{
                onScroll: (event: React.SyntheticEvent) => {
                  const listboxNode = event.currentTarget;
                  if (
                    listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
                    hasMoreShifts &&
                    !shiftsLoading
                  ) {
                    handleLoadMoreShifts();
                  }
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search and select a shift..."
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.875rem',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      borderRadius: '5px',
                      '& fieldset': {
                        borderRadius: '5px',
                      },
                      '&:hover': {
                        backgroundColor: 'white',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white',
                      },
                    },
                  }}
                />
              )}
              sx={{
                '& .MuiAutocomplete-inputRoot': {
                  paddingRight: '14px !important',
                },
              }}
            />
          </div>
        </div>

        {/* Right Column - Shift Details */}
        {selectedShift && (
          <div className="bg-white rounded-lg border border-primaryColor/20 shadow-sm p-2.5">
            <div className="grid grid-cols-2 gap-4">
              {/* First Column */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-semibold text-gray-900 truncate">{selectedShift.name}</h4>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border flex-shrink-0 ${getStatusColor((selectedShift.status || 'draft') as any)}`}>
                    {selectedShift.status
                      ? selectedShift.status.charAt(0).toUpperCase() + selectedShift.status.slice(1)
                      : 'Draft'}
                  </span>
                </div>
                {selectedShift.shiftTemplateId && (
                  <div className="mt-0.5">
                    <span className="text-[10px] font-bold text-primaryColor bg-primaryColor/10 px-2 py-0.5 rounded-md inline-flex flex-wrap gap-1 items-center">
                      <span className="opacity-80">ID:</span>
                      <span className="font-mono">
                        {selectedShift.shiftTemplateId.slice(0, 8)}
                      </span>
                      {selectedShift.shiftTemplateName && (
                        <>
                          <span className="opacity-60">•</span>
                          <span className="opacity-80">Template:</span>
                          <span>{selectedShift.shiftTemplateName}</span>
                        </>
                      )}
                    </span>
                  </div>
                )}
                {selectedShift.zone && (
                  <div className="flex items-center gap-1 text-xs text-gray-600">
                    <LocationIcon sx={{ fontSize: '12px' }} />
                    <span>{selectedShift.zone}</span>
                  </div>
                )}
                <div className="flex items-center gap-1 text-xs text-gray-600">
                  <ScheduleIcon sx={{ fontSize: '12px' }} />
                  <span>{formatDateTime(selectedShift.startTime)} - {formatDateTime(selectedShift.endTime)}</span>
                </div>
              </div>

              {/* Second Column */}
              <div className="flex flex-col items-end justify-start gap-1.5">
                <div className="flex items-center gap-2">
                  <PeopleIcon sx={{ fontSize: '14px', color: '#6B7280' }} />
                  <span className="text-sm font-semibold text-gray-900">{assignedCount}/{maxDrivers}</span>
                </div>
                <div className="flex items-center gap-2 w-full justify-end">
                  <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${utilizationRate >= 100 ? 'bg-red-500' : utilizationRate >= 80 ? 'bg-amber-500' : 'bg-green-500'
                        }`}
                      style={{ width: `${Math.min(utilizationRate, 100)}%` }}
                    />
                  </div>
                  <span className={`text-xs font-medium ${remainingSlots > 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                    {remainingSlots > 0 ? `${remainingSlots} available` : 'Full'}
                  </span>
                </div>
                {selectedDriverIds.length > 0 && (
                  <CustomButton
                    variant="contained"
                    color="primary"
                    onClick={onAssignDrivers}
                    disabled={!selectedShiftId || selectedDriverIds.length === 0 || (maxDrivers > 0 && remainingSlots < selectedDriverIds.length)}
                    additionalStyles="px-3 py-1 text-xs shadow-sm hover:shadow-md transition-all mt-1 w-fit"
                  >
                    <AddIcon sx={{ fontSize: '14px', marginRight: '4px' }} />
                    Assign {selectedDriverIds.length}
                  </CustomButton>
                )}
              </div>
            </div>
            {selectedDriverIds.length > 0 && maxDrivers > 0 && remainingSlots < selectedDriverIds.length && (
              <p className="text-[10px] text-red-600 mt-1.5 text-right">
                Only {remainingSlots} slot{remainingSlots !== 1 ? 's' : ''} left
              </p>
            )}
            {!selectedShift && (
              <p className="text-[10px] text-yellow-600 mt-1.5 text-right">
                Shift data loading...
              </p>
            )}
          </div>
        )}
      </div>

      {selectedShiftId && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4 lg:h-[calc(100vh-300px)]">
            <div className="lg:col-span-1 space-y-4 flex flex-col min-h-0">
              <div className="bg-gradient-to-br from-white to-primaryColor/5 rounded-xl border border-gray-200/60 shadow-sm p-4">
                <div className="flex items-center gap-2 mb-3">
                  <ScheduleIcon sx={{ fontSize: '18px', color: '#3B82F6' }} />
                  <h3 className="text-sm font-semibold text-gray-900">{selectedShift?.name}</h3>
                </div>
                <div className="space-y-2 text-xs text-gray-600">
                  <div className="flex items-center justify-between">
                    <span>Status:</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${getStatusColor((selectedShift?.status || 'draft') as any)}`}>
                      {selectedShift?.status
                        ? selectedShift.status.charAt(0).toUpperCase() + selectedShift.status.slice(1)
                        : 'Draft'}
                    </span>
                  </div>
                  {selectedShift?.zone && (
                    <div className="flex items-center justify-between">
                      <span>Zone:</span>
                      <span className="font-medium text-gray-900">{selectedShift.zone}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span>Start:</span>
                    <span className="font-medium text-gray-900">{formatDateTime(selectedShift?.startTime || '')}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>End:</span>
                    <span className="font-medium text-gray-900">{formatDateTime(selectedShift?.endTime || '')}</span>
                  </div>
                  <div className="pt-2 border-t border-gray-200/60">
                    <div className="flex items-center justify-between mb-1.5">
                      <span>Capacity:</span>
                      <span className="font-bold text-gray-900">{assignedCount}/{maxDrivers}</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200/60 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${utilizationRate >= 100 ? 'bg-red-500' : utilizationRate >= 80 ? 'bg-amber-500' : 'bg-green-500'
                          }`}
                        style={{ width: `${Math.min(utilizationRate, 100)}%` }}
                      />
                    </div>
                    <div className="text-[9px] text-gray-500 mt-1">
                      {remainingSlots > 0 ? `${remainingSlots} slot${remainingSlots !== 1 ? 's' : ''} available` : 'Full capacity'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-white to-blue-50/30 rounded-xl border border-gray-200/60 shadow-sm p-4 flex-1 flex flex-col min-h-0">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <PeopleIcon sx={{ fontSize: '18px', color: '#3B82F6' }} />
                    <h3 className="text-sm font-semibold text-gray-900">
                      Assigned Drivers
                    </h3>
                    <Tooltip
                      title="Drivers currently assigned to this shift. Assignment statuses: Pending (not acknowledged), Confirmed (acknowledged), Checked In (started shift), Completed (finished shift). Drivers who have checked in cannot be unassigned."
                      arrow
                    >
                      <InfoIcon sx={{ fontSize: '13px', color: '#9CA3AF', cursor: 'help' }} />
                    </Tooltip>
                  </div>
                  <span className="text-xs font-bold text-gray-900 bg-blue-100 px-2 py-0.5 rounded-md">
                    {assignedCount}/{maxDrivers}
                  </span>
                </div>
                {assignedDriversForShift.length === 0 ? (
                  <div className="text-center py-8 bg-white/60 rounded-lg border border-gray-200/60 flex-1 flex items-center justify-center">
                    <div>
                      <PeopleIcon sx={{ fontSize: '32px', color: '#9CA3AF', marginBottom: '8px' }} />
                      <p className="text-xs font-medium text-gray-600 mb-1">No drivers assigned</p>
                      <p className="text-[10px] text-gray-400">Select drivers above to assign them</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 flex-1 overflow-y-auto pr-1 min-h-0" style={{ maxHeight: '100%' }}>
                    {assignments
                      .filter(a => a.shiftId === selectedShiftId)
                      .map(assignment => {
                        const courier = assignment.courier;
                        if (!courier) return null;

                        const courierName = courier.firstName && courier.lastName
                          ? `${courier.firstName} ${courier.lastName}`
                          : courier.firstName || courier.lastName || 'Unknown';

                        const assignmentStatus: AssignmentStatus = assignment.status || 'pending';
                        const isCancelled = assignmentStatus === 'cancelled';
                        const canUnassign = !isCancelled && assignmentStatus !== 'checked_in' && assignmentStatus !== 'completed';

                        return (
                          <div key={assignment.id} className={`p-3 rounded-lg border transition-all ${isCancelled
                            ? 'bg-gray-100/60 border-gray-300/60 opacity-60 cursor-not-allowed'
                            : 'bg-white/80 border-gray-200/60 hover:shadow-sm'
                            }`}>
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                {/* ID and Driver Name in first line */}
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  {courier.id && (
                                    <span className={`text-xs ${isCancelled ? 'text-gray-400' : 'text-gray-500'
                                      }`}>
                                      <span className="font-medium">ID</span> <span className="font-mono">{courier.id}</span>
                                    </span>
                                  )}
                                  {courier.id && (
                                    <span className="text-gray-300">•</span>
                                  )}
                                  <h4 className={`text-sm font-semibold truncate ${isCancelled ? 'text-gray-500' : 'text-gray-900'
                                    }`}>{courierName}</h4>
                                </div>

                                {/* Phone Number and Assignment Status in same line */}
                                <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                  {courier.mobileNumber && (
                                    <>
                                      <div className="flex items-center gap-1">
                                        <PhoneIcon sx={{ fontSize: '11px', color: isCancelled ? '#9CA3AF' : '#6B7280' }} />
                                        <span className={`text-xs truncate ${isCancelled ? 'text-gray-400' : 'text-gray-600'
                                          }`}>{courier.mobileNumber}</span>
                                      </div>
                                      <span className="text-gray-300">•</span>
                                    </>
                                  )}
                                  <div className="flex items-center gap-1.5">
                                    <span className={`text-[10px] font-medium uppercase tracking-wide ${isCancelled ? 'text-gray-400' : 'text-gray-500'
                                      }`}>Assignment:</span>
                                    <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-semibold border ${getStatusColor(assignmentStatus)}`}>
                                      {assignmentStatus.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    </span>
                                  </div>
                                </div>

                                {/* Assigned Date */}
                                {assignment.createdAt && (
                                  <div className="text-[9px] text-gray-400 mt-1">
                                    Assigned {formatDate(assignment.createdAt)}
                                  </div>
                                )}
                              </div>

                              {/* Unassign Button */}
                              <div className="flex-shrink-0">
                                {!isCancelled && canUnassign && (
                                  <Tooltip title="Unassign driver from this shift" arrow>
                                    <button
                                      onClick={async () => {
                                        if (window.confirm(`Are you sure you want to unassign ${courierName} from this shift?`)) {
                                          try {
                                            await onUnassignDriver(selectedShiftId, courier.id);
                                          } catch (error) {
                                            console.error('Error unassigning driver:', error);
                                          }
                                        }
                                      }}
                                      className="px-3 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-all border border-red-200 hover:border-red-300 flex items-center gap-1.5"
                                    >
                                      <DeleteIcon sx={{ fontSize: '14px' }} />
                                      <span>Unassign</span>
                                    </button>
                                  </Tooltip>
                                )}
                                {!isCancelled && !canUnassign && (
                                  <Tooltip title="Cannot unassign: Driver has checked in or completed the shift" arrow>
                                    <button
                                      disabled
                                      className="px-3 py-1.5 text-xs font-medium text-gray-400 cursor-not-allowed rounded-md border border-gray-200 flex items-center gap-1.5"
                                    >
                                      <DeleteIcon sx={{ fontSize: '14px' }} />
                                      <span>Unassign</span>
                                    </button>
                                  </Tooltip>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>

            <div className="lg:col-span-2 flex flex-col min-h-0">
              <div className="bg-gradient-to-br from-white to-gray-50/30 rounded-xl border border-gray-200/60 shadow-sm p-4 flex flex-col flex-1 min-h-0">
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="text-base font-semibold text-gray-900">Select Drivers</h3>
                  <Tooltip title="Choose drivers to assign. Use search and filters to find drivers quickly." arrow>
                    <HelpIcon sx={{ fontSize: '14px', color: '#9CA3AF', cursor: 'help' }} />
                  </Tooltip>
                </div>

                <PreAssignedDriversSelector
                  selectedDriverIds={selectedDriverIds}
                  onDriverIdsChange={handleDriverIdsChange}
                  drivers={availableDrivers}
                  driversLoading={driversLoading}
                  hasMoreDrivers={hasMoreDrivers}
                  onLoadMoreDrivers={onLoadMoreDrivers}
                  externalSearchQuery={driverSearchQuery}
                  onExternalSearchChange={onDriverSearchChange}
                  assignedDriverIds={assignedDriversForShift}
                  assignedDriverStatuses={assignedDriverStatuses}
                  remainingSlots={remainingSlots}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
